// WebGL2 stage compositor — "Tấm gương chung" (shared mirror), v0.5.
//
// Every device renders the SAME stage from the same inputs: background + every
// person decoded from packed [RGB | mask | seq-barcode] frames, split into
// body-part layers ordered by the deterministic interaction solver. The whole
// stage is mirrored ONCE for display (one convention for everybody, including
// yourself — there is no separate self view). Poses travel un-mirrored.
//
// Per person per frame (SA B6):
//   1. upload the packed frame; read its seq from the barcode strip (20×1 FBO + readPixels)
//      and join the pose message of exactly that frame (PoseRing; falls back to latest)
//   2. matte (texture space, proc resolution):
//        guided filter: mask guided by the luma of the colour image (He et al.,
//        coefficients at ½ res in RGBA16F, box r = 4, ε = GF_EPS)
//        push-pull foreground: mip pyramid of (I·w, w) of the interior → F estimate,
//        edge band uses F instead of the camera colour (decontamination, F·α)
//        seam-safe: colour/mask are sampled clamped ≥ 2 px away from the seam/strip
//   3. layers in solver order (arms_back / body / head / arms_front): analytic capsule /
//      ellipse SDF masks in source px, Reinhard Lab colour matching toward the common
//      target (temporally smoothed colorStats), light wrap (blurred background in the
//      edge band), soft contact shadow on the partner under a touching hand, soft-z
//      cross-fade only inside the overlap while two people swap depth
//   4. soft ground shadow ellipse under visible feet; coaching ghosts on top
//
// ─────────────────────────────── INTERFACE CONTRACT (UI) ───────────────────────────────
//   stage.setLayout(state: LayoutState)
//       LayoutState = { version; template: 'side'|'heads'|'shoulder'; mirror: boolean;
//                       slots: { peerId; x; front; name? }[]; mirrorPhoto: boolean; giver? }
//       slots[].x is DISPLAY space (as seen in the mirror, 0..1 left→right); `front`
//       higher = nearer (override on top of the body-driven depth). Older versions are ignored.
//   stage.onInteraction(cb: (s: InteractionState) => void): () => void
//       InteractionState = { contacts: {a; b; kind: 'hand-shoulder'|'head-head'}[];
//                            ready: Record<id, boolean>; hints: Record<id, string>;
//                            depthHints: Record<id, string> }       (on change, ≤ 10 Hz)
//   stage.setCoaching(on: boolean)   ghost targets (pink dashed; green when matched)
//   stage.perf(): { fps; mlMs; composeMs }
//   stage.snapshot(): { decision, layout } | null      (frozen at the shutter for the still)
//   Types: import { LayoutState, InteractionState } from '../booth/solver'.
//   Layer identity: set StageLayer.peerId (room.selfId / room.partnerId) and
//   StageLayer.poses (pipe.poses / room.remotePoses). When omitted they are resolved
//   automatically (pipeline canvas → local pipeline + room.selfId, <video> → room
//   partner) via media/registry.ts, so the pre-v0.5 booth keeps working unchanged.
//   Legacy fields (face, slot, mirrored, noFace, sceneSlot) still work: without a
//   pose the person is placed by `face` and drawn as one flat layer.
// ─────────────────────────────────────────────────────────────────────────────────────────

import { colorPlan, reinhardXform, smoothStats } from '../booth/color';
import {
  AnchorFilter, anchorFromFace, placeAll, type EyeAnchor, slotX, stageXOf, type FaceBox, type PlaceInput, type Placement, type PoseAdjust,
} from '../booth/layout';
import { ALPHA_HI, ALPHA_LO, EDGE_HI, EDGE_LO, GF_EPS } from '../booth/matte';
import { anchorOf, armGeometry, headEllipse, solverPerson } from '../booth/parts';
import { BAR_CELLS, BAR_H, KP, PoseRing, decodeBarcode, kpV, kpX, kpY, type ColorStats, type PoseFrame, type Side } from '../booth/pose';
import {
  InteractionSolver, armsFor, defaultLayout, sameInteraction,
  type ContactPoint, type Decision, type Ghost, type InteractionState, type LayerKind, type LayoutState,
} from '../booth/solver';
import { STAGE_H, STAGE_W } from '../booth/themes';
import { perfCounters, registry } from '../media/registry';
import { cssFilterMatrix, isIdentity, type ColorMatrix } from '../booth/grade';
import { NO_VIEW, anchorBottom, applyView, bodyBox, groupView, smoothView, type GroupView } from '../booth/layout';
import { hullBounds } from '../booth/bodygate';

export type { InteractionState, LayoutState };

export interface StageLayer {
  id: string;
  source: HTMLVideoElement | HTMLCanvasElement;
  /** legacy face box (used when no pose is available) */
  face: FaceBox;
  adjust: PoseAdjust;
  /** legacy slot index (used when no LayoutState is set) */
  slot: number;
  /** informational only: the stage is mirrored as a whole (v0.5) */
  mirrored: boolean;
  noFace: boolean;
  /** v0.5: peer id used by the LayoutState and the solver */
  peerId?: string;
  /** v0.5: pose frames keyed by seq (pipe.poses / room.remotePoses) */
  poses?: PoseRing;
  /** v0.7 local layer: seq of the frame in `source` (skips the barcode readback) */
  seq?: () => number | null;
  /** v0.7 local layer: changes when `source` holds a new frame (else no re-upload / re-matte) */
  frameId?: () => number;
}

const LAYER_CODE: Record<LayerKind, number> = { arms_back: 0, body: 1, head: 2, arms_front: 3 };
const K_FLAT = 4, K_BACKDROP = 5;
/** light-wrap strength (10–30 %) and contact-shadow opacity (15–30 %) */
const WRAP = 0.2;
const CONTACT_SHADOW = 0.24;
const MAX_CAPS = 6;

// ------------------------------------------------------------------ shaders

const VS_TEX = `#version 300 es
in vec2 a_pos;
out vec2 v_uv;
void main() { v_uv = a_pos; gl_Position = vec4(a_pos * 2.0 - 1.0, 0.0, 1.0); }`;

/** helpers shared by the texture-space passes: seam-safe sampling of the packed frame */
const PACKED = `
uniform sampler2D u_packed;
uniform vec4 u_geo;   // colour w, h, texture W, H (px)
uniform vec2 u_edge;  // codec cleanup of the mask
vec2 cUV(vec2 s) { s = clamp(s, vec2(0.5), u_geo.xy - vec2(2.5)); return s / u_geo.zw; }
vec2 mUV(vec2 s) { s = clamp(s, vec2(2.5, 0.5), u_geo.xy - vec2(2.5)); return (s + vec2(u_geo.x, 0.0)) / u_geo.zw; }
float rawA(vec2 s) { return smoothstep(u_edge.x, u_edge.y, texture(u_packed, mUV(s)).r); }
float luma(vec3 c) { return dot(c, vec3(0.299, 0.587, 0.114)); }
`;

const FS_PUSH = `#version 300 es
precision highp float;
${PACKED}
in vec2 v_uv; out vec4 o;
void main() {
  vec2 s = v_uv * u_geo.xy;
  vec3 I = texture(u_packed, cUV(s)).rgb;
  float w = smoothstep(0.85, 0.97, rawA(s));
  o = vec4(I * w, w);
}`;

const FS_GF1 = `#version 300 es
precision highp float;
${PACKED}
in vec2 v_uv; out vec4 o;
void main() {
  vec2 s = v_uv * u_geo.xy;
  float I = luma(texture(u_packed, cUV(s)).rgb);
  float p = rawA(s);
  o = vec4(I, p, I * p, I * I);
}`;

const FS_BOX = `#version 300 es
precision highp float;
uniform sampler2D u_in;
uniform vec2 u_step;
in vec2 v_uv; out vec4 o;
void main() {
  vec4 s = vec4(0.0);
  for (int k = -4; k <= 4; k++) s += texture(u_in, v_uv + u_step * float(k));
  o = s / 9.0;
}`;

const FS_AB = `#version 300 es
precision highp float;
uniform sampler2D u_in;
in vec2 v_uv; out vec4 o;
void main() {
  vec4 m = texture(u_in, v_uv);
  float a = (m.z - m.x * m.y) / (max(m.w - m.x * m.x, 0.0) + ${GF_EPS.toFixed(6)});
  o = vec4(a, m.y - a * m.x, 0.0, 1.0);
}`;

const FS_MATTE = `#version 300 es
precision highp float;
${PACKED}
uniform sampler2D u_ab;
uniform sampler2D u_push;
uniform int u_gf;
uniform int u_decon;
in vec2 v_uv; out vec4 o;
void main() {
  vec2 s = v_uv * u_geo.xy;
  vec3 I = texture(u_packed, cUV(s)).rgb;
  float a;
  if (u_gf == 1) { vec2 ab = texture(u_ab, v_uv).xy; a = clamp(ab.x * luma(I) + ab.y, 0.0, 1.0); }
  else a = rawA(s);
  a = smoothstep(${ALPHA_LO.toFixed(3)}, ${ALPHA_HI.toFixed(3)}, a);
  // push-pull foreground estimate (interior colour pulled into the edge band)
  vec4 p2 = textureLod(u_push, v_uv, 2.0), p4 = textureLod(u_push, v_uv, 4.0), p6 = textureLod(u_push, v_uv, 6.0);
  vec3 F = p2.a > 0.05 ? p2.rgb / p2.a : p4.a > 0.02 ? p4.rgb / p4.a : p6.a > 0.002 ? p6.rgb / p6.a : I;
  if (u_decon == 0) F = I;
  F = mix(F, I, smoothstep(${EDGE_LO.toFixed(3)}, ${EDGE_HI.toFixed(3)}, a));
  o = vec4(F * a, a);
}`;

const FS_BAR = `#version 300 es
precision highp float;
uniform sampler2D u_packed;
uniform vec4 u_bar; // cell width px, strip centre y px, texture W, H
out vec4 o;
void main() {
  float i = floor(gl_FragCoord.x);
  vec2 p = vec2((i + 0.5) * u_bar.x, u_bar.y);
  o = vec4(texture(u_packed, p / u_bar.zw).rgb, 1.0);
}`;

const VS_COMP = `#version 300 es
in vec2 a_pos;
uniform vec2 u_srcSize;
uniform mat3 u_M;
uniform vec2 u_stage;
uniform float u_flip;
out vec2 v_src;
out vec2 v_stage;
void main() {
  vec2 s = a_pos * u_srcSize;
  vec2 p = (u_M * vec3(s, 1.0)).xy;
  v_src = s; v_stage = p;
  vec2 c = p / u_stage * 2.0 - 1.0;
  gl_Position = vec4(c.x * u_flip, -c.y, 0.0, 1.0);
}`;

/** colour filter of the booth (CSS filter chain as a matrix, booth/grade.ts) — last pass */
const FS_GRADE = `#version 300 es
precision highp float;
uniform sampler2D u_tex;
uniform mat3 u_m;
uniform vec3 u_o;
in vec2 v_uv; out vec4 o;
void main() { vec3 c = texture(u_tex, v_uv).rgb; o = vec4(clamp(u_m * c + u_o, 0.0, 1.0), 1.0); }`;

const FS_COMP = `#version 300 es
precision highp float;
uniform sampler2D u_matte;   // premultiplied (F·α, α), mipmapped — or the packed frame for the backdrop
uniform sampler2D u_bg;      // background, mipmapped (light wrap)
uniform sampler2D u_other;   // the person underneath (soft-z)
uniform vec2 u_srcSize;
uniform vec2 u_stage;
uniform float u_flip;
uniform vec4 u_geo;
uniform int u_kind;
uniform vec4 u_cap[${MAX_CAPS}];
uniform vec2 u_capRG[${MAX_CAPS}];
uniform vec4 u_head;
uniform float u_headRot;
uniform float u_feather;
uniform int u_color;
uniform vec3 u_cScale;
uniform vec3 u_cOff;
uniform float u_wrap;
uniform vec4 u_contact[4];
uniform int u_nContact;
uniform mat3 u_otherInv;
uniform vec2 u_otherSize;
uniform float u_softW;
uniform int u_cutOther;
in vec2 v_src;
in vec2 v_stage;
out vec4 o;

float capCov(int i) {
  vec2 a = u_cap[i].xy, b = u_cap[i].zw;
  vec2 pa = v_src - a, ba = b - a;
  float h = clamp(dot(pa, ba) / max(dot(ba, ba), 1e-4), 0.0, 1.0);
  return 1.0 - smoothstep(-u_feather, u_feather, length(pa - ba * h) - u_capRG[i].x);
}
float group(float g) {
  float m = 0.0;
  for (int i = 0; i < ${MAX_CAPS}; i++) if (abs(u_capRG[i].y - g) < 0.5) m = max(m, capCov(i));
  return m;
}
float headCov() {
  vec2 d = v_src - u_head.xy;
  float c = cos(u_headRot), s = sin(u_headRot);
  vec2 q = vec2(c * d.x + s * d.y, -s * d.x + c * d.y) / u_head.zw;
  float f = u_feather / min(u_head.z, u_head.w);
  return 1.0 - smoothstep(1.0 - f, 1.0 + f, length(q));
}
// sRGB <-> CIELAB (same constants as booth/color.ts)
vec3 lin(vec3 c) { return mix(c / 12.92, pow((c + 0.055) / 1.055, vec3(2.4)), step(0.04045, c)); }
vec3 gam(vec3 c) { c = max(c, 0.0); return mix(12.92 * c, 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c)); }
vec3 fl(vec3 t) { return mix(7.787 * t + 16.0 / 116.0, pow(t, vec3(1.0 / 3.0)), step(0.008856, t)); }
vec3 finv(vec3 t) { return mix((t - 16.0 / 116.0) / 7.787, t * t * t, step(0.206893, t)); }
vec3 toLab(vec3 c) {
  vec3 l = lin(c);
  vec3 xyz = vec3(dot(l, vec3(0.4124, 0.3576, 0.1805)) / 0.95047, dot(l, vec3(0.2126, 0.7152, 0.0722)), dot(l, vec3(0.0193, 0.1192, 0.9505)) / 1.08883);
  vec3 f = fl(xyz);
  return vec3(116.0 * f.y - 16.0, 500.0 * (f.x - f.y), 200.0 * (f.y - f.z));
}
vec3 fromLab(vec3 L) {
  float fy = (L.x + 16.0) / 116.0;
  vec3 xyz = finv(vec3(fy + L.y / 500.0, fy, fy - L.z / 200.0)) * vec3(0.95047, 1.0, 1.08883);
  vec3 rgb = vec3(dot(xyz, vec3(3.2406, -1.5372, -0.4986)), dot(xyz, vec3(-0.9689, 1.8758, 0.0415)), dot(xyz, vec3(0.0557, -0.204, 1.057)));
  return clamp(gam(rgb), 0.0, 1.0);
}

void main() {
  vec2 uv = v_src / u_srcSize;
  if (uv.x < 0.0 || uv.y < 0.0 || uv.x > 1.0 || uv.y > 1.0) discard;
  if (u_kind == ${K_BACKDROP}) {
    vec2 s = clamp(v_src, vec2(0.5), u_geo.xy - vec2(2.5));
    o = vec4(texture(u_matte, s / u_geo.zw).rgb, 1.0);
    return;
  }
  vec4 m = texture(u_matte, uv);
  if (m.a < 0.002) discard;
  vec3 F = m.rgb / m.a;
  float k = 1.0;
  if (u_kind == 0) k = group(2.0);
  else if (u_kind == 1) {
    float cut = group(2.0);
    if (u_cutOther == 1 && cut > 0.0) {
      vec2 os = (u_otherInv * vec3(v_stage, 1.0)).xy / u_otherSize;
      cut *= (os.x < 0.0 || os.y < 0.0 || os.x > 1.0 || os.y > 1.0) ? 0.0 : texture(u_other, os).a;
    }
    k = 1.0 - cut;
  }
  else if (u_kind == 2) k = headCov();
  else if (u_kind == 3) k = group(1.0);
  if (k < 0.002) discard;
  if (u_color == 1) F = fromLab(toLab(F) * u_cScale + u_cOff);
  if (u_wrap > 0.0) {
    float ab = textureLod(u_matte, uv, 3.0).a;
    vec2 bgUV = vec2(u_flip < 0.0 ? 1.0 - v_stage.x / u_stage.x : v_stage.x / u_stage.x, v_stage.y / u_stage.y);
    vec3 bgb = textureLod(u_bg, bgUV, 4.0).rgb;
    F = mix(F, bgb, u_wrap * clamp(1.0 - ab, 0.0, 1.0));
  }
  float sh = 0.0;
  for (int i = 0; i < 4; i++) {
    if (i >= u_nContact) break;
    vec2 d = v_stage - u_contact[i].xy;
    sh = max(sh, u_contact[i].w * exp(-dot(d, d) / (u_contact[i].z * u_contact[i].z)));
  }
  F *= 1.0 - sh;
  if (u_softW < 1.0) {
    vec2 os = (u_otherInv * vec3(v_stage, 1.0)).xy / u_otherSize;
    float oa = (os.x < 0.0 || os.y < 0.0 || os.x > 1.0 || os.y > 1.0) ? 0.0 : texture(u_other, os).a;
    k *= mix(1.0, u_softW, oa);
  }
  float A = m.a * k;
  o = vec4(F * A, A);
}`;

const VS_SHAPE = `#version 300 es
in vec2 a_pos;
uniform vec4 u_rect;
uniform vec2 u_stage;
uniform float u_flip;
out vec2 v_stage;
void main() {
  vec2 p = u_rect.xy + a_pos * u_rect.zw;
  v_stage = p;
  vec2 c = p / u_stage * 2.0 - 1.0;
  gl_Position = vec4(c.x * u_flip, -c.y, 0.0, 1.0);
}`;

const FS_SHAPE = `#version 300 es
precision highp float;
uniform int u_mode;     // 0 = background texture, 1 = ground shadow ellipse, 2 = ghost ring
uniform sampler2D u_tex;
uniform vec2 u_stage;
uniform vec4 u_shape;   // centre x, y, radius x, radius y
uniform vec4 u_col;     // premultiplied colour
uniform float u_time;
in vec2 v_stage;
out vec4 o;
void main() {
  if (u_mode == 0) { o = vec4(texture(u_tex, v_stage / u_stage).rgb, 1.0); return; }
  vec2 d = (v_stage - u_shape.xy) / u_shape.zw;
  float r = length(d);
  if (u_mode == 1) { o = u_col * (1.0 - smoothstep(0.25, 1.0, r)); return; }
  float px = 1.0 / u_shape.z;
  float ring = 1.0 - smoothstep(2.0 * px, 3.5 * px, abs(r - 1.0));
  float ang = atan(d.y, d.x);
  float dash = u_col.a > 0.99 ? 1.0 : step(0.45, fract(ang * 3.8197 + u_time * 0.4));
  float fill = (1.0 - smoothstep(0.9, 1.0, r)) * 0.14;
  float a = max(ring * dash, fill);
  o = vec4(u_col.rgb, 1.0) * a;
}`;

// ------------------------------------------------------------------ GL helpers

function compile(gl: WebGL2RenderingContext, type: number, src: string) {
  const s = gl.createShader(type)!;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) ?? 'shader');
  return s;
}

interface Prog { p: WebGLProgram; u: (n: string) => WebGLUniformLocation | null }

function program(gl: WebGL2RenderingContext, vs: string, fs: string): Prog {
  const p = gl.createProgram()!;
  gl.attachShader(p, compile(gl, gl.VERTEX_SHADER, vs));
  gl.attachShader(p, compile(gl, gl.FRAGMENT_SHADER, fs));
  gl.bindAttribLocation(p, 0, 'a_pos');
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p) ?? 'link');
  const cache = new Map<string, WebGLUniformLocation | null>();
  return { p, u: (n) => { if (!cache.has(n)) cache.set(n, gl.getUniformLocation(p, n)); return cache.get(n)!; } };
}

interface Target { tex: WebGLTexture; fbo: WebGLFramebuffer; w: number; h: number }

const srcSize = (s: HTMLVideoElement | HTMLCanvasElement) =>
  s instanceof HTMLVideoElement ? { w: s.videoWidth, h: s.videoHeight } : { w: s.width, h: s.height };

/** Per person GPU state + smoothed inputs. */
interface PersonGL {
  packed: WebGLTexture;
  push: Target | null;
  matte: Target | null;
  gfA: Target | null;
  gfB: Target | null;
  w: number;
  h: number;
  /** barcode detection: frames in a row with a valid strip (v2 packing) */
  barOk: number;
  barMiss: number;
  seq: number | null;
  anchorF: AnchorFilter;
  color: ColorStats | null;
  /** <video> sources: frames presented (requestVideoFrameCallback) vs frames processed */
  watched: HTMLVideoElement | null;
  rvfc: boolean;
  vframes: number;
  done: number;
  /** last uploaded frame id of a local (worker) layer */
  localFrame: number;
  last: { w: number; h: number; masked: boolean } | null;
}

interface Frame {
  layer: StageLayer;
  id: string;
  g: PersonGL;
  pose: PoseFrame | null;
  place: Placement;
  srcW: number;
  srcH: number;
}

export class Stage {
  readonly canvas: HTMLCanvasElement;
  layers: StageLayer[] = [];
  /** this member's camera frame is the backdrop (not cut out); null = virtual background */
  sceneSlot: number | null = null;
  /** v0.9.3: colour stats of the scene owner (the people are matched to them) */
  private sceneColor: import('../booth/pose').ColorStats | null = null;
  /** individual effects can be switched off (quality ladder, A/B checks in the demo) */
  readonly features = { guided: true, decontaminate: true, color: true, wrap: true, shadows: true, parts: true };

  private gl: WebGL2RenderingContext;
  private quad: WebGLVertexArrayObject;
  private pPush: Prog; private pGf1: Prog; private pBox: Prog; private pAb: Prog; private pMatte: Prog;
  private pBar: Prog; private pComp: Prog; private pShape: Prog;
  private pGrade: Prog;
  /** backing-store scale of the screen canvas */
  private res = 1;
  private grade: ColorMatrix | null = null;
  /** virtual camera of the group (smoothed) */
  private view: GroupView = NO_VIEW;
  private viewT = 0;
  private gradeT: Target | null = null;
  private floatOk: boolean;
  private bgTex: WebGLTexture;
  private hasBg = false;
  private barT: Target;
  private barPx = new Uint8Array(BAR_CELLS * 4);
  private people = new Map<string, PersonGL>();
  private raf = 0;
  private quietUntil = 0;

  private layout: LayoutState | null = null;
  private solver = new InteractionSolver();
  private listeners = new Set<(s: InteractionState) => void>();
  private lastEmitted: InteractionState | null = null;
  private lastEmitAt = 0;
  private pendingIt: InteractionState | null = null;
  private coaching = false;
  private lastDecision: Decision | null = null;
  private ghosts: Ghost[] = [];
  private contactPts: ContactPoint[] = [];
  private lastAnchors: Record<string, EyeAnchor> = {};
  private fpsN = 0;
  /** frames composed since start (device tests) */
  framesDrawn = 0;
  private fpsT0 = performance.now();
  private fpsV = 0;
  private composeMs = 0;

  constructor() {
    this.canvas = document.createElement('canvas');
    // the stage keeps its 720×960 coordinate system; on dense phone screens it is drawn
    // at 1.5× so the people and edges stay sharp (all shaders are resolution independent)
    this.res = typeof devicePixelRatio === 'number' && devicePixelRatio >= 2 ? 1.5 : 1;
    this.canvas.width = Math.round(STAGE_W * this.res);
    this.canvas.height = Math.round(STAGE_H * this.res);
    const gl = this.canvas.getContext('webgl2', { premultipliedAlpha: true, antialias: false, alpha: false, preserveDrawingBuffer: false });
    if (!gl) throw new Error('Thiết bị không hỗ trợ WebGL2');
    this.gl = gl;
    this.floatOk = !!(gl.getExtension('EXT_color_buffer_float') || gl.getExtension('EXT_color_buffer_half_float'));
    this.pPush = program(gl, VS_TEX, FS_PUSH);
    this.pGf1 = program(gl, VS_TEX, FS_GF1);
    this.pBox = program(gl, VS_TEX, FS_BOX);
    this.pAb = program(gl, VS_TEX, FS_AB);
    this.pMatte = program(gl, VS_TEX, FS_MATTE);
    this.pBar = program(gl, VS_TEX, FS_BAR);
    this.pComp = program(gl, VS_COMP, FS_COMP);
    this.pShape = program(gl, VS_SHAPE, FS_SHAPE);
    this.pGrade = program(gl, VS_TEX, FS_GRADE);
    this.quad = gl.createVertexArray()!;
    gl.bindVertexArray(this.quad);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    this.bgTex = this.newTex(true);
    this.barT = this.target(BAR_CELLS, 1, false, false);
    registry.stage = this;
  }

  // ---------------------------------------------------------------- public API (contract)

  setLayout(state: LayoutState) {
    if (this.layout && state.version < this.layout.version) return;
    this.layout = state;
  }

  onInteraction(cb: (s: InteractionState) => void): () => void {
    this.listeners.add(cb);
    if (this.lastEmitted) cb(this.lastEmitted);
    return () => { this.listeners.delete(cb); };
  }

  setCoaching(on: boolean) {
    this.coaching = on;
  }

  perf(): { fps: number; mlMs: number; composeMs: number } {
    return { fps: this.fpsV, mlMs: perfCounters.mlMs, composeMs: this.composeMs };
  }

  /** Decision + layout of the last drawn frame (the still uses exactly this, US7). */
  snapshot(): { decision: Decision; layout: LayoutState } | null {
    if (!this.lastDecision) return null;
    const layout = structuredClone(this.currentLayout());
    const decision: Decision = {
      ...structuredClone(this.lastDecision), softZ: null,
      slots: layout.slots, mirror: layout.mirror !== false, mirrorPhoto: layout.mirrorPhoto !== false,
      natural: layout.scale !== 'matched',
      view: { ...this.view },
      anchors: { ...this.lastAnchors },
    };
    return { decision, layout };
  }

  /** Last solver contact points (un-mirrored stage px), e.g. for UI effects. */
  contacts(): ContactPoint[] { return this.contactPts; }

  setBackground(c: HTMLCanvasElement) {
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.bgTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, c);
    gl.generateMipmap(gl.TEXTURE_2D);
    this.hasBg = true;
  }

  /**
   * Draw loop capped at ~30 fps: the cameras deliver 30 frames/s, so drawing at the
   * display rate (120 Hz phones) only burns the UI thread and the GPU.
   */
  /** Booth colour filter as a CSS filter chain ("none" = off), applied in the last WebGL pass. */
  setFilter(css: string | undefined) {
    const m = cssFilterMatrix(css);
    this.grade = isIdentity(m) ? null : m;
  }

  start() {
    let last = 0;
    const loop = (t: number) => {
      this.raf = requestAnimationFrame(loop);
      if (t - last < 1000 / 31) return;
      last = t;
      this.draw();
    };
    this.raf = requestAnimationFrame(loop);
  }

  stop() {
    cancelAnimationFrame(this.raf);
  }

  /** Skip drawing until `untilMs` (frees the main thread right at the shutter instant). */
  quiet(untilMs: number) {
    this.quietUntil = untilMs;
  }

  dispose() {
    this.stop();
    if (registry.stage === this) registry.stage = null;
    (this.gl.getExtension('WEBGL_lose_context') as any)?.loseContext();
  }

  // ---------------------------------------------------------------- GL resources

  private newTex(mip = false) {
    const gl = this.gl;
    const t = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, mip ? gl.LINEAR_MIPMAP_LINEAR : gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return t;
  }

  private target(w: number, h: number, mip: boolean, float: boolean): Target {
    const gl = this.gl;
    const tex = this.newTex(mip);
    if (float) gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA16F, w, h, 0, gl.RGBA, gl.HALF_FLOAT, null);
    else gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    if (mip) gl.generateMipmap(gl.TEXTURE_2D);
    const fbo = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return { tex, fbo, w, h };
  }

  private free(t: Target | null) {
    if (!t) return;
    this.gl.deleteTexture(t.tex);
    this.gl.deleteFramebuffer(t.fbo);
  }

  private personGL(id: string): PersonGL {
    let g = this.people.get(id);
    if (!g) {
      g = {
        packed: this.newTex(), push: null, matte: null, gfA: null, gfB: null, w: 0, h: 0,
        barOk: 0, barMiss: 0, seq: null, anchorF: new AnchorFilter(), color: null,
        watched: null, rvfc: false, vframes: 0, done: -1, localFrame: -1, last: null,
      };
      this.people.set(id, g);
    }
    return g;
  }

  private ensureSize(g: PersonGL, w: number, h: number) {
    if (g.w === w && g.h === h && g.matte) return;
    [g.push, g.matte, g.gfA, g.gfB].forEach((t) => this.free(t));
    g.w = w; g.h = h;
    g.push = this.target(w, h, true, false);
    g.matte = this.target(w, h, true, false);
    if (this.floatOk) {
      const hw = Math.max(1, w >> 1), hh = Math.max(1, h >> 1);
      g.gfA = this.target(hw, hh, false, true);
      g.gfB = this.target(hw, hh, false, true);
    }
  }

  private pass(p: Prog, t: Target | null, setup: () => void) {
    const gl = this.gl;
    gl.useProgram(p.p);
    gl.bindFramebuffer(gl.FRAMEBUFFER, t ? t.fbo : null);
    gl.viewport(0, 0, t ? t.w : this.canvas.width, t ? t.h : this.canvas.height);
    setup();
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  private bind(unit: number, tex: WebGLTexture) {
    const gl = this.gl;
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, tex);
  }

  // ---------------------------------------------------------------- per person preprocessing

  /** Reads the barcode strip of the uploaded packed frame; null when there is none (v1 sender). */
  private readSeq(g: PersonGL, TW: number, TH: number): number | null {
    const gl = this.gl;
    const h = TH - BAR_H;
    if (h < 16) return null;
    this.pass(this.pBar, this.barT, () => {
      this.bind(0, g.packed);
      gl.uniform1i(this.pBar.u('u_packed'), 0);
      gl.uniform4f(this.pBar.u('u_bar'), Math.floor(TW / BAR_CELLS), h + BAR_H / 2, TW, TH);
    });
    gl.readPixels(0, 0, BAR_CELLS, 1, gl.RGBA, gl.UNSIGNED_BYTE, this.barPx);
    const lum = Array.from({ length: BAR_CELLS }, (_, i) => this.barPx[i * 4] * 0.299 + this.barPx[i * 4 + 1] * 0.587 + this.barPx[i * 4 + 2] * 0.114);
    return decodeBarcode(lum);
  }

  /** Upload + barcode + matte. Returns the colour-area size (source px). */
  private prepare(l: StageLayer, g: PersonGL, masked: boolean): { w: number; h: number } | null {
    const gl = this.gl;
    const { w: TW, h: TH } = srcSize(l.source);
    if (!TW || !TH) return null;
    // a <video> only changes ~30×/s while we draw at display rate: skip upload, barcode
    // readback and matting when no new frame was presented since the last time
    const v = l.source instanceof HTMLVideoElement ? l.source : null;
    if (v !== g.watched) {
      g.watched = v;
      g.done = -1;
      g.rvfc = !!v && 'requestVideoFrameCallback' in v;
      if (v && g.rvfc) {
        const tick = () => { if (g.watched !== v) return; g.vframes++; v.requestVideoFrameCallback(tick); };
        v.requestVideoFrameCallback(tick);
      }
    }
    if (v && g.rvfc && g.done === g.vframes && g.last && g.last.masked === masked && g.matte) return g.last;
    g.done = g.vframes;
    if (l.frameId) {
      const id = l.frameId();
      if (id === g.localFrame && g.last && g.last.masked === masked && g.matte) return g.last;
      g.localFrame = id;
    }
    this.bind(0, g.packed);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, l.source);

    // v2 packing (barcode strip) is detected from the frames themselves; the local
    // worker pipeline tells us the seq directly (no GPU→CPU readback stall)
    const known = l.seq ? l.seq() : undefined;
    const seq = known !== undefined && known !== null ? known : this.readSeq(g, TW, TH);
    if (seq != null) { g.barOk = Math.min(g.barOk + 1, 30); g.barMiss = 0; } else if (++g.barMiss > 20) g.barOk = 0;
    // three valid strips in a row establish v2 packing; it is dropped only after 20 misses
    const v2 = g.barOk >= 3;
    g.seq = v2 ? seq : null;
    const w = Math.floor(TW / 2), h = v2 ? TH - BAR_H : TH;
    g.last = { w, h, masked };
    if (!masked) return g.last;
    this.ensureSize(g, w, h);
    const remote = l.source instanceof HTMLVideoElement;
    const setPacked = (p: Prog) => {
      this.bind(0, g.packed);
      gl.uniform1i(p.u('u_packed'), 0);
      gl.uniform4f(p.u('u_geo'), w, h, TW, TH);
      // local layer is lossless → wide ramp; remote went through the codec → drop near-zero noise
      gl.uniform2f(p.u('u_edge'), remote ? 0.06 : 0.0, remote ? 0.94 : 1.0);
    };
    gl.disable(gl.BLEND);
    this.pass(this.pPush, g.push, () => setPacked(this.pPush));
    this.bind(0, g.push!.tex);
    gl.generateMipmap(gl.TEXTURE_2D);
    const gf = !!(g.gfA && g.gfB && this.features.guided);
    if (gf && g.gfA && g.gfB) {
      const A = g.gfA, B = g.gfB;
      const step = (t: Target, dx: number, dy: number) => () => {
        this.bind(1, t.tex);
        gl.uniform1i(this.pBox.u('u_in'), 1);
        gl.uniform2f(this.pBox.u('u_step'), dx / t.w, dy / t.h);
      };
      this.pass(this.pGf1, g.gfA, () => setPacked(this.pGf1));
      this.pass(this.pBox, g.gfB, step(A, 1, 0));
      this.pass(this.pBox, g.gfA, step(B, 0, 1));
      this.pass(this.pAb, g.gfB, () => { this.bind(1, g.gfA!.tex); gl.uniform1i(this.pAb.u('u_in'), 1); });
      this.pass(this.pBox, g.gfA, step(B, 1, 0));
      this.pass(this.pBox, g.gfB, step(A, 0, 1));
    }
    this.pass(this.pMatte, g.matte, () => {
      setPacked(this.pMatte);
      this.bind(1, (gf ? g.gfB : g.push)!.tex);
      gl.uniform1i(this.pMatte.u('u_ab'), 1);
      this.bind(2, g.push!.tex);
      gl.uniform1i(this.pMatte.u('u_push'), 2);
      gl.uniform1i(this.pMatte.u('u_gf'), gf ? 1 : 0);
      gl.uniform1i(this.pMatte.u('u_decon'), this.features.decontaminate ? 1 : 0);
    });
    this.bind(0, g.matte!.tex);
    gl.generateMipmap(gl.TEXTURE_2D);
    return g.last;
  }

  // ---------------------------------------------------------------- per frame

  /** Who is this layer (peer id) and where do its poses come from. */
  private resolve(l: StageLayer): { id: string; ring: PoseRing | null } {
    if (l.peerId || l.poses) return { id: l.peerId ?? l.id, ring: l.poses ?? null };
    if (l.source instanceof HTMLCanvasElement) {
      for (const p of registry.pipelines) if (p.packed === l.source) return { id: registry.room?.selfId ?? l.id, ring: p.poses };
    } else if (registry.room) {
      return { id: registry.room.partnerId ?? l.id, ring: registry.room.remotePoses };
    }
    return { id: l.id, ring: null };
  }

  private currentLayout(): LayoutState {
    if (this.layout) return this.layout;
    // legacy: slots from the layers' slot indices (display space), mirrored stage
    const ids = [...this.layers].sort((a, b) => a.slot - b.slot).map((l) => this.resolve(l).id);
    return defaultLayout(ids);
  }

  /** Draws one frame now (normally driven by start()). */
  draw() {
    if (performance.now() < this.quietUntil) return;
    const t0 = performance.now();
    const gl = this.gl;
    const layout = this.currentLayout();
    const mirror = layout.mirror !== false;
    const flip = mirror ? -1 : 1;

    // ---- 1. inputs
    const frames: Frame[] = [];
    const ready = this.layers.filter((l) => {
      const { w, h } = srcSize(l.source);
      return w && h && !(l.source instanceof HTMLVideoElement && l.source.readyState < 2);
    });
    const ordered = [...ready].sort((a, b) => a.slot - b.slot);
    const scene = this.sceneSlot == null ? null : ready.find((l) => l.slot === this.sceneSlot) ?? null;
    const inputs: PlaceInput[] = [];
    for (const l of ready) {
      const { id, ring } = this.resolve(l);
      const g = this.personGL(l.id);
      const size = this.prepare(l, g, l !== scene);
      if (!size) continue;
      const pose = ring ? ring.get(g.seq) : null;
      const t = performance.now();
      const anchor = g.anchorF.filter(anchorOf(pose, l.face), t);
      g.color = smoothStats(g.color, pose?.color ?? null);
      const slot = layout.slots.find((s) => s.peerId === id);
      const others = ordered.filter((o) => o !== scene);
      const dispX = slot ? slot.x : slotX(Math.max(0, others.indexOf(l)), Math.max(1, others.length));
      inputs.push({
        srcW: size.w, srcH: size.h, anchor, adjust: l.adjust, slotX: stageXOf(dispX, mirror),
        scene: l === scene, noFace: l.noFace, natural: layout.scale !== 'matched',
      });
      frames.push({ layer: l, id, g, pose, place: null as unknown as Placement, srcW: size.w, srcH: size.h });
    }
    // depth-driven perspective: whoever is in front gets up to +10 % (B5.1)
    const depth = this.lastDecision?.depth ?? {};
    const zs = frames.map((f) => depth[f.id]).filter((z): z is number => z != null);
    if (zs.length > 1 && layout.scale === 'matched') {
      const zMax = Math.max(...zs);
      frames.forEach((f, i) => { if (depth[f.id] != null) inputs[i].extraZoom = 1 + Math.min(0.1, (zMax - depth[f.id]) * 0.5); });
    }
    const placed = placeAll(STAGE_W, STAGE_H, inputs, mirror);
    frames.forEach((f, i) => { f.place = placed[i]; });
    // virtual camera: frame the group (real scale only, no scene backdrop)
    const masked = frames.filter((f) => f.place.masked);
    if (layout.scale !== 'matched' && !scene && masked.length) {
      const boxes = masked.map((f) => bodyBox(f.place, hullBounds(f.pose?.kp ?? null, f.srcW, f.srcH)));
      const now = performance.now();
      this.view = smoothView(this.view, groupView(boxes, STAGE_W, STAGE_H), this.viewT ? now - this.viewT : 1e4);
      this.viewT = now;
      for (const f of masked) f.place = anchorBottom(applyView(f.place, this.view, STAGE_W, STAGE_H, f.srcW, f.srcH), f.srcW, f.srcH, STAGE_H);
    } else { this.view = NO_VIEW; this.viewT = 0; }
    this.lastAnchors = Object.fromEntries(frames.map((f, i) => [f.id, inputs[i].anchor]));

    // ---- 2. solver
    const people = frames.filter((f) => f.place.masked);
    const solverIn = people.map((f) => {
      const slot = layout.slots.find((s) => s.peerId === f.id);
      return solverPerson(f.id, f.pose, inputs[frames.indexOf(f)].anchor, f.place, f.srcW, f.srcH, STAGE_H, slot?.front ?? 0);
    });
    const out = this.solver.update(solverIn, layout, t0, STAGE_H);
    this.lastDecision = out.decision;
    this.ghosts = out.ghosts;
    this.contactPts = out.contactPoints;
    this.emit(out.interaction, t0);

    // ---- 3. composite (into an offscreen target when a colour filter is active)
    if (this.grade) this.gradeT ??= this.target(this.canvas.width, this.canvas.height, false, false);
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.grade && this.gradeT ? this.gradeT.fbo : null);
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.disable(gl.BLEND);
    // background (never mirrored: virtual backgrounds may carry text)
    gl.useProgram(this.pShape.p);
    this.bind(0, this.bgTex);
    this.shapeCommon(1);
    gl.uniform1i(this.pShape.u('u_tex'), 0);
    gl.uniform1i(this.pShape.u('u_mode'), 0);
    gl.uniform4f(this.pShape.u('u_rect'), 0, 0, STAGE_W, STAGE_H);
    if (this.hasBg) gl.drawArrays(gl.TRIANGLES, 0, 6);
    else { gl.clearColor(1, 1, 1, 1); gl.clear(gl.COLOR_BUFFER_BIT); }
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    // the member whose real room is the backdrop: full frame, mirrored with the people
    this.sceneColor = frames.find((x) => !x.place.masked)?.g.color ?? null;
    for (const f of frames.filter((x) => !x.place.masked)) this.drawLayer(f, K_BACKDROP, flip, people, out.decision, [], 0);

    // ground shadows under visible feet
    if (this.features.shadows) for (const f of people) this.groundShadow(f, flip);

    // part layers in solver order; a duplicate layer (head / arms_front) is only drawn
    // when another person's layer lies between it and its own body
    const byId = new Map(people.map((f) => [f.id, f]));
    let prevId: string | null = null;
    for (const ref of out.decision.order) {
      const f = byId.get(ref.id);
      if (!f) continue;
      const arms = this.features.parts ? armsFor(out.decision, f.id) : armsFor(null, f.id);
      const group = ref.kind === 'arms_front' ? 'front' : ref.kind === 'arms_back' ? 'back' : null;
      if (group && !(['left', 'right'] as Side[]).some((s) => arms[s].state === group)) continue;
      if ((ref.kind === 'head' || ref.kind === 'arms_front') && prevId === f.id) continue;
      const flat = !f.pose?.kp && ref.kind === 'body';
      const contacts = (ref.kind === 'body' || ref.kind === 'head') ? out.contactPoints.filter((c) => c.b === f.id && c.kind === 'hand-shoulder') : [];
      this.drawLayer(f, flat ? K_FLAT : LAYER_CODE[ref.kind], flip, people, out.decision, contacts, t0);
      prevId = f.id;
    }

    if (this.coaching) for (const gh of this.ghosts) this.drawGhost(gh, flip, t0);

    // ---- 4. colour filter: one matrix pass to the screen
    if (this.grade && this.gradeT) {
      const g = this.grade, src = this.gradeT;
      gl.disable(gl.BLEND);
      this.pass(this.pGrade, null, () => {
        this.bind(0, src.tex);
        gl.uniform1i(this.pGrade.u('u_tex'), 0);
        gl.uniformMatrix3fv(this.pGrade.u('u_m'), true, g.m);
        gl.uniform3f(this.pGrade.u('u_o'), ...g.o);
      });
    }

    // ---- perf
    const dt = performance.now() - t0;
    this.composeMs = this.composeMs ? this.composeMs * 0.9 + dt * 0.1 : dt;
    this.fpsN++;
    this.framesDrawn++;
    if (t0 - this.fpsT0 >= 1000) { this.fpsV = (this.fpsN * 1000) / (t0 - this.fpsT0); this.fpsN = 0; this.fpsT0 = t0; }
  }

  private shapeCommon(flip: number) {
    const gl = this.gl;
    gl.uniform2f(this.pShape.u('u_stage'), STAGE_W, STAGE_H);
    gl.uniform1f(this.pShape.u('u_flip'), flip);
  }

  private groundShadow(f: Frame, flip: number) {
    const p = f.pose;
    if (!p?.kp) return;
    const feet = [KP.leftAnkle, KP.rightAnkle].filter((i) => kpV(p, i) > 0.6);
    if (!feet.length) return;
    const pts = feet.map((i) => {
      const [x, y] = [kpX(p, i) * f.srcW, kpY(p, i) * f.srcH];
      const c = Math.cos(f.place.rot), s = Math.sin(f.place.rot);
      const dx = (x - f.place.ax) * f.place.s, dy = (y - f.place.ay) * f.place.s;
      return [f.place.px + c * dx - s * dy, f.place.py + s * dx + c * dy];
    });
    const cx = pts.reduce((a, q) => a + q[0], 0) / pts.length, cy = Math.max(...pts.map((q) => q[1])) + 4;
    if (cy > STAGE_H + 40) return;
    const rx = 0.12 * STAGE_H, ry = 0.025 * STAGE_H;
    const gl = this.gl;
    gl.useProgram(this.pShape.p);
    this.shapeCommon(flip);
    gl.uniform1i(this.pShape.u('u_mode'), 1);
    gl.uniform4f(this.pShape.u('u_rect'), cx - rx, cy - ry, 2 * rx, 2 * ry);
    gl.uniform4f(this.pShape.u('u_shape'), cx, cy, rx, ry);
    gl.uniform4f(this.pShape.u('u_col'), 0, 0, 0, 0.28);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  private drawGhost(gh: Ghost, flip: number, t: number) {
    const gl = this.gl;
    gl.useProgram(this.pShape.p);
    this.shapeCommon(flip);
    const r = gh.r + 4;
    gl.uniform1i(this.pShape.u('u_mode'), 2);
    gl.uniform4f(this.pShape.u('u_rect'), gh.x - r, gh.y - r, 2 * r, 2 * r);
    gl.uniform4f(this.pShape.u('u_shape'), gh.x, gh.y, gh.r, gh.r);
    // pink dashed (#ff5fa2) while coaching, solid green (#36c97a) when matched
    if (gh.matched) gl.uniform4f(this.pShape.u('u_col'), 0.21, 0.79, 0.48, 1);
    else gl.uniform4f(this.pShape.u('u_col'), 1, 0.37, 0.64, 0.9);
    gl.uniform1f(this.pShape.u('u_time'), t / 1000);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  private drawLayer(f: Frame, kind: number, flip: number, people: Frame[], d: Decision, contacts: ContactPoint[], _t: number) {
    const gl = this.gl;
    const P = this.pComp, u = P.u;
    const pl = f.place;
    gl.useProgram(P.p);
    const c = Math.cos(pl.rot) * pl.s, s = Math.sin(pl.rot) * pl.s;
    // column-major mat3: src px → stage px
    gl.uniformMatrix3fv(u('u_M'), false, [c, s, 0, -s, c, 0, pl.px - c * pl.ax + s * pl.ay, pl.py - s * pl.ax - c * pl.ay, 1]);
    gl.uniform2f(u('u_srcSize'), f.srcW, f.srcH);
    gl.uniform2f(u('u_stage'), STAGE_W, STAGE_H);
    gl.uniform1f(u('u_flip'), flip);
    gl.uniform1i(u('u_kind'), kind);
    const { w: TW, h: TH } = srcSize(f.layer.source);
    gl.uniform4f(u('u_geo'), f.srcW, f.srcH, TW, TH);
    this.bind(0, kind === K_BACKDROP ? f.g.packed : f.g.matte!.tex);
    gl.uniform1i(u('u_matte'), 0);
    this.bind(1, this.bgTex);
    gl.uniform1i(u('u_bg'), 1);
    gl.uniform1i(u('u_other'), 2);
    this.bind(2, f.g.matte?.tex ?? f.g.packed);
    if (kind === K_BACKDROP) { gl.drawArrays(gl.TRIANGLES, 0, 6); return; }

    // body-part geometry (source px)
    const caps = new Float32Array(MAX_CAPS * 4), rg = new Float32Array(MAX_CAPS * 2);
    const arms = this.features.parts ? armsFor(d, f.id) : armsFor(null, f.id);
    let n = 0;
    for (const side of ['left', 'right'] as Side[]) {
      const st = arms[side].state;
      if (st === 'none') continue;
      const geo = armGeometry(f.pose, side, f.srcW, f.srcH, st === 'back');
      if (!geo) continue;
      const grp = st === 'front' ? 1 : 2;
      const add = (ax: number, ay: number, bx: number, by: number, r: number) => {
        if (n >= MAX_CAPS) return;
        caps.set([ax, ay, bx, by], n * 4); rg.set([r, grp], n * 2); n++;
      };
      add(geo.upper.ax, geo.upper.ay, geo.upper.bx, geo.upper.by, geo.upper.r);
      if (geo.fore) add(geo.fore.ax, geo.fore.ay, geo.fore.bx, geo.fore.by, geo.fore.r);
      if (geo.hand) add(geo.hand.x, geo.hand.y, geo.hand.x, geo.hand.y, geo.hand.r);
    }
    for (let i = n; i < MAX_CAPS; i++) rg[i * 2 + 1] = -1;
    gl.uniform4fv(u('u_cap'), caps);
    gl.uniform2fv(u('u_capRG'), rg);
    const anchor = f.g.anchorF.value ?? anchorFromFace(f.layer.face);
    const he = headEllipse(anchor, f.srcW, f.srcH);
    gl.uniform4f(u('u_head'), he.cx, he.cy, he.rx, he.ry);
    gl.uniform1f(u('u_headRot'), he.rot);
    gl.uniform1f(u('u_feather'), 1.2 / pl.s);

    // Reinhard colour matching toward the common target
    // toward the scene owner's tone when a real room is the scene (the backdrop itself is never changed)
    const plan = colorPlan(people.map((p) => p.g.color), this.sceneColor);
    const target = plan.target;
    const x = reinhardXform(f.g.color, target, plan.strength, plan.chroma);
    gl.uniform1i(u('u_color'), target && f.g.color && this.features.color && kind !== K_BACKDROP ? 1 : 0);
    gl.uniform3fv(u('u_cScale'), x.scale);
    gl.uniform3fv(u('u_cOff'), x.offset);
    gl.uniform1f(u('u_wrap'), this.hasBg && this.sceneSlot == null && this.features.wrap ? WRAP : 0);

    // contact shadow of the touching hand on this person (slightly below the hand: light from above)
    const cs = new Float32Array(16);
    contacts.slice(0, 4).forEach((cp, i) => {
      const giver = people.find((p) => p.id === cp.a);
      const r = giver ? Math.max(12, (solverSw(giver) ?? 80) * 0.32) : 24;
      cs.set([cp.x + r * 0.15, cp.y + r * 0.35, r, CONTACT_SHADOW], i * 4);
    });
    gl.uniform4fv(u('u_contact'), cs);
    gl.uniform1i(u('u_nContact'), this.features.shadows ? Math.min(4, contacts.length) : 0);

    const bindOther = (o: Frame) => {
      const up = o.place;
      const uc = Math.cos(up.rot), us = Math.sin(up.rot);
      // stage px → the other person's source px
      const a = uc / up.s, b = -us / up.s, cc = us / up.s, dd = uc / up.s;
      gl.uniformMatrix3fv(u('u_otherInv'), false, [a, b, 0, cc, dd, 0,
        up.ax - (a * up.px + cc * up.py), up.ay - (b * up.px + dd * up.py), 1]);
      gl.uniform2f(u('u_otherSize'), o.srcW, o.srcH);
      this.bind(2, o.g.matte!.tex);
    };
    // body with an arm behind a partner: cut the arm out only where the partner is
    // (no partition seam on the owner's own shoulder; arms_back fills the rest underneath)
    const backPartner = kind === LAYER_CODE.body
      ? people.find((p) => p.g.matte && (['left', 'right'] as Side[]).some((sd) => arms[sd].state === 'back' && arms[sd].partner === p.id))
      : null;
    gl.uniform1i(u('u_cutOther'), backPartner ? 1 : 0);
    // soft-z cross-fade inside the overlap while a depth swap is pending
    const sz = d.softZ;
    const under = sz && sz.over === f.id ? people.find((p) => p.id === sz.under) : null;
    if (backPartner) bindOther(backPartner);
    else if (sz && under?.g.matte) bindOther(under);
    gl.uniform1f(u('u_softW'), !backPartner && sz && under?.g.matte ? sz.w : 1);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  private emit(it: InteractionState, t: number) {
    if (!this.listeners.size) { this.lastEmitted = it; return; }
    const changed = !sameInteraction(this.pendingIt ?? this.lastEmitted, it);
    if (changed) this.pendingIt = it;
    if (this.pendingIt && t - this.lastEmitAt >= 100) {
      const s = this.pendingIt;
      this.pendingIt = null;
      if (sameInteraction(this.lastEmitted, s)) return;
      this.lastEmitted = s;
      this.lastEmitAt = t;
      this.listeners.forEach((cb) => cb(s));
    }
  }
}

/** shoulder width in stage px of a frame's person (for contact-shadow size) */
function solverSw(f: Frame): number | null {
  const p = f.pose;
  if (!p?.kp || kpV(p, KP.leftShoulder) < 0.35 || kpV(p, KP.rightShoulder) < 0.35) return null;
  const dx = (kpX(p, KP.leftShoulder) - kpX(p, KP.rightShoulder)) * f.srcW, dy = (kpY(p, KP.leftShoulder) - kpY(p, KP.rightShoulder)) * f.srcH;
  return Math.hypot(dx, dy) * f.place.s;
}
